import clsx from "clsx";
import { MinusCircle } from "react-feather";
import { Tag, Tooltip, type TagTone } from "@bfc-software/pallet";
import type { TFunction } from "i18next";

import type { TaskStatus } from "../-data/types";
import type { PlanStatusKey } from "../-data/plans-model";

import styles from "./shared.module.css";

/**
 * The small repeated pieces of a task row: progress, tags, status, identity.
 *
 * Held together in one file rather than one per component because none of them
 * stands alone - they only ever appear inside a row or a card, and splitting them
 * would spread five files across two consumers to no benefit.
 */

/**
 * How each on-screen status reads as a tone.
 *
 * Exhaustive over `TaskStatus`, so a status added to the union fails to
 * compile here rather than rendering as an untoned tag nobody notices. The
 * union is exactly the activation listing's four statuses, and every task
 * `TaskStatusTag` is asked to draw a tone for is a released one - a planned
 * task has no `status` field at all (`Task`'s `planned` arm), so it never
 * reaches this component; it draws `NotReleasedMark` instead. That is still
 * true tab-wide for the All tasks tab, which reads the activation listing
 * exclusively - see the `TaskStatus` docblock in `types.ts`.
 */
const STATUS_TONES: Record<TaskStatus, TagTone> = {
  unassigned: "warning",
  assigned: "info",
  claimed: "info",
  completed: "success",
};

/** The i18n key under `tasks.status` for each status. */
const STATUS_LABEL_KEYS: Record<TaskStatus, string> = {
  unassigned: "unassigned",
  assigned: "assigned",
  claimed: "claimed",
  completed: "completed",
};

const PLAN_STATUS_TONES: Record<PlanStatusKey, TagTone> = {
  notActivated: "neutral",
  // An activation that failed needs the manager's eye, not a neutral tag that
  // reads the same as a plan nobody has touched yet.
  activationFailed: "danger",
  activating: "info",
  activated: "info",
  completed: "success",
};

export function TaskStatusTag({
  status,
  t,
}: {
  status: TaskStatus;
  t: TFunction;
}) {
  return (
    // Tag stamps its own data-test-role, so the row-level hook goes on a wrapper.
    <span data-test-role="task-status" data-status={status}>
      <Tag
        label={t(`tasks.status.${STATUS_LABEL_KEYS[status]}`)}
        display={{ tone: STATUS_TONES[status] }}
      />
    </span>
  );
}

/**
 * A task nobody has released, in the Status column.
 *
 * An icon rather than a `Tag`, and deliberately not a fifth `TaskStatus`. Every
 * chip in this column is a claim about the floor, and "nothing has been asked
 * of this task yet" is a statement about the plan above it - giving it chip
 * shape would put it in the same vocabulary as `Unassigned` and invite it to be
 * read as a status a selector could act on. Not silent either: the icon is
 * focusable through pallet's `Tooltip`, which carries the explanation, and a
 * visually hidden label names it for a screen reader since a tooltip is only a
 * description. Mirrors `PickStatusCell`'s `unknown` arm in
 * `task-detail-drawer.tsx`, and deliberately does not look like it - `unknown`
 * means "we could not find out" and reads as danger; this means "nothing has
 * happened yet" and must not be mistaken for something having gone wrong.
 */
export function NotReleasedMark({ t }: { t: TFunction }) {
  return (
    <span data-test-role="task-status" data-status="not-released">
      <Tooltip content={t("tasks.row.notReleasedTooltip")}>
        <MinusCircle size={15} aria-hidden="true" />
        <span className={styles.srOnly}>{t("tasks.row.notReleased")}</span>
      </Tooltip>
    </span>
  );
}

export function PlanStatusTag({
  statusKey,
  t,
}: {
  statusKey: PlanStatusKey;
  t: TFunction;
}) {
  return (
    <span data-test-role="plan-status" data-status={statusKey}>
      <Tag
        label={t(`tasks.planStatus.${statusKey}`)}
        display={{ tone: PLAN_STATUS_TONES[statusKey] }}
      />
    </span>
  );
}

/**
 * Picks done against picks total.
 *
 * The bar and the count always come from the same two numbers - a bar without
 * the count cannot distinguish 1/2 from 500/1000, and the count without the bar
 * cannot be scanned down a column.
 */
export function Progress({
  done,
  total,
  percent,
  wide = false,
  label,
}: {
  done: number;
  total: number;
  percent: number;
  /** The card's meter fills its column; a row's is a fixed width. */
  wide?: boolean;
  /** Pre-formatted `n/n picks`. Omitted in tables, which show `n/n`. */
  label?: string;
}) {
  return (
    <div
      className={clsx(styles.progress, wide && styles.progressWide)}
      data-test-role="progress"
    >
      <div className={clsx(styles.track, wide && styles.trackWide)}>
        <div
          className={clsx(styles.fill, percent === 100 && styles.fillComplete)}
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className={styles.progressLabel}>
        {label ?? `${done}/${total}`}
      </span>
    </div>
  );
}

/**
 * A short list of warehouse labels - currently a plan's route codes.
 *
 * Auto-toned, and named after what it does rather than after its caller: the
 * labels come from the warehouse, so there is no fixed set to assign tones to,
 * and hashing the label at least keeps a given one the same colour everywhere it
 * appears.
 */
export function AutoTags({ labels }: { labels: ReadonlyArray<string> }) {
  return (
    <div className={styles.tagRow}>
      {labels.map((label) => (
        <Tag key={label} label={label} display={{ tone: "auto" }} />
      ))}
    </div>
  );
}

/** The task's identity: the number said out loud, over the routes it serves. */
export function TaskIdentity({
  taskId,
  routeCodes,
  t,
}: {
  taskId: string;
  /**
   * Every route the task picks from. A merged plan's task spans several
   * (ADR-0013), and listing them is the honest answer - naming one of them would
   * be picking a winner, and naming none would hide that the task serves any.
   */
  routeCodes: ReadonlyArray<string>;
  t: TFunction;
}) {
  return (
    <div className={styles.taskCell}>
      <span className={styles.taskId}>
        {t("tasks.row.task", { id: taskId })}
      </span>
      <span className={styles.routeId}>
        {routeCodes.length > 0
          ? t("tasks.row.route", { id: routeCodes.join(", ") })
          : t("tasks.row.none")}
      </span>
    </div>
  );
}
